import { DropdownContainer, Menu, UnreadDot } from '@lib/components'
import type { ID } from '@octopus-do/octopus-project-core'
import { Badge } from '@octopus-do/octopus-storybook'
import { Plus as PlusIcon } from '@octopus-do/octopus-storybook/icons'
import type { Workspace } from '@store/reducers/dashboard/types'
import { countWorkspaceProjects } from '@store/reducers/dashboard/utils'
import { selectUnreadByWorkspace } from '@store/reducers/inbox'
import type React from 'react'
import { useSelector } from 'react-redux'

import { clsJoin, getBadgeType } from '@/utils'
import { WorkspaceAvatar } from '../WorkspaceAvatar'
import {
  DropdownButton,
  DropdownButtonMobile,
  TeamSpaceTutorial,
} from './components'
import css from './styles.module.css'
import { useWorkspacesDropdown } from './useWorkspacesDropdown'

interface Props {
  current: Workspace
  workspaces: Workspace[]
  onCloseSidebar: () => void
}

export const WorkspacesDropdown: React.FC<Props> = ({
  workspaces,
  onCloseSidebar,
  current,
}) => {
  const {
    offsetY,
    desktop,
    showCreateTeamBtn,
    handleOnClick,
    handleCreateSpace,
  } = useWorkspacesDropdown()
  const unreadByWorkspace = useSelector(selectUnreadByWorkspace)

  const menuInnerCn = (id: ID) =>
    clsJoin(css.menu_inner, id === current.id && css.active)

  return (
    <DropdownContainer
      customClassName={css.dropdown}
      position={!desktop ? 'bottom' : 'bottom-start'}
      offset={{ y: offsetY }}
      renderButton={(onSetRef, onClick, isOpen) =>
        !desktop ? (
          <DropdownButtonMobile
            onClick={onClick}
            onSetRef={onSetRef}
            open={isOpen}
            avatar={current.avatar}
            title={current?.title}
          />
        ) : (
          <DropdownButton
            avatar={current.avatar}
            title={current?.title}
            open={isOpen}
            onSetRef={onSetRef}
            onClick={onClick}
          />
        )
      }
    >
      {(closeDropdown) => (
        <>
          {workspaces.map((workspace) => (
            <Menu
              key={workspace.id}
              customClassName={css.menu_item}
              active={workspace.id === current.id}
              onClick={() => {
                handleOnClick(workspace.id)
                closeDropdown()
                onCloseSidebar()
              }}
            >
              <div className={menuInnerCn(workspace.id)}>
                <WorkspaceAvatar url={workspace.avatar} />

                <div className={css.text_wrap}>
                  <div className={css.title}>
                    {workspace.title}
                    {(unreadByWorkspace[workspace.id] ?? 0) > 0 && (
                      <UnreadDot className={css.unread_dot} />
                    )}
                  </div>
                  <div className={css.description}>
                    {countWorkspaceProjects(workspace)} projects
                  </div>
                </div>

                {(workspace.type !== 'personal' ||
                  workspace.planType === 'free' ||
                  workspace.planType === 'pro') && (
                  <div className={css.badge_wrap}>
                    <Badge
                      size="large"
                      type={getBadgeType(workspace.planType, workspace.expired)}
                    />
                  </div>
                )}
              </div>
            </Menu>
          ))}

          <div className={clsJoin(css.hidden, showCreateTeamBtn && css.show)}>
            <div className={css.divider} />

            <div className={css.create_space_btn} onClick={handleCreateSpace}>
              <PlusIcon className={css.icon} />
              <span className={css.btn_name}>Create team space</span>
              <div className={css.badge_wrap}>
                <Badge size="large" type="team" />
              </div>
            </div>

            <TeamSpaceTutorial />
          </div>
        </>
      )}
    </DropdownContainer>
  )
}
